import { Delete, Get, Patch, Post, Put } from "../Axios/AxiosFunctions";
import { useMutation, useQuery } from "@tanstack/react-query";

import { apiHeader } from "../config/apiUrl";
import { useSelector } from "react-redux";
import { useState } from "react";

/**
 * @deprecated use HttpFunctions instead.
 */

// For Get API
const getData = async ({ accessToken, apiURL, responseType }) => {
  const response = await Get({
    route: apiURL,
    accessToken,
    showAlert: true,
    responseType,
  });
  return response.status >= 400 ? response?.response : response?.data;
};

export const useGet = ({
  enabled = true,
  apiURL,
  queryKey = null,
  staleTime = 30000,
  keepPreviousData = false,
  responseType,
}) => {
  const { accessToken } = useSelector((state) => state?.authReducer);
  const [isLoading, setIsLoading] = useState(false);

  return {
    ...useQuery({
      queryKey: queryKey || [apiURL],
      queryFn: async () => {
        setIsLoading(true);
        try {
          return await getData({ accessToken, apiURL, responseType });
        } finally {
          setIsLoading(false);
        }
      },
      enabled,
      keepPreviousData,
      staleTime,
    }),
    isLoading,
  };
};

// For Post API
const postData = async ({ accessToken, apiURL, isFormData, apiBody }) => {
  const [response, isSuccess] = await Post({
    route: apiURL,
    data: apiBody,
    headers: apiHeader(accessToken, isFormData),
  });

  return [response?.data, isSuccess];
};

export const usePost = ({
  onSuccess,
  apiURL,
  isFormData = false,
  ...props
}) => {
  const { accessToken } = useSelector((state) => state?.authReducer);
  const [isLoading, setIsLoading] = useState(false);

  const mutation = useMutation({
    mutationFn: async (apiBody) => {
      setIsLoading(true);
      try {
        const [apiResponse, isSuccess] = await postData({
          apiBody,
          accessToken,
          apiURL,
          isFormData,
        });
        if (!isSuccess) {
          throw apiResponse;
        }
        return apiResponse;
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess,
    ...props,
  });

  return { ...mutation, isLoading };
};

// For Patch API
const updateData = async ({ accessToken, apiURL, isFormData, apiBody }) => {
  const response = await Patch({
    route: apiURL,
    data: apiBody,
    headers: apiHeader(accessToken, isFormData),
  });
  return response.status >= 400 ? response?.response : response?.data;
};

export const usePatch = ({
  onSuccess,
  apiURL,
  isFormData = false,
  ...props
}) => {
  const { accessToken } = useSelector((state) => state?.authReducer);
  const [isLoading, setIsLoading] = useState(false);

  const mutation = useMutation({
    mutationFn: async (apiBody) => {
      setIsLoading(true);
      try {
        return await updateData({
          apiBody,
          accessToken,
          apiURL,
          isFormData,
        });
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess,
    ...props,
  });

  return { ...mutation, isLoading };
};

// For Put API
const putData = async ({ accessToken, apiURL, isFormData, apiBody }) => {
  const response = await Put({
    route: apiURL,
    data: apiBody,
    headers: apiHeader(accessToken, isFormData),
  });

  return response.status >= 400 ? response?.response : response?.data;
};

export const usePut = ({ onSuccess, apiURL, isFormData = false, ...props }) => {
  const { accessToken } = useSelector((state) => state?.authReducer);
  const [isLoading, setIsLoading] = useState(false);

  const mutation = useMutation({
    mutationFn: async (apiBody) => {
      setIsLoading(true);
      try {
        return await putData({
          apiBody,
          accessToken,
          apiURL,
          isFormData,
        });
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess,
    ...props,
  });

  return { ...mutation, isLoading };
};

// For Delete API
const deleteData = async ({ accessToken, apiURL, isFormData, ...apiBody }) => {
  const response = await Delete({
    route: apiURL,
    data: apiBody,
    headers: apiHeader(accessToken, isFormData).headers,
  });

  return response.status >= 400 ? response?.response : response?.data;
};

export const useDelete = ({ onSuccess, apiURL, isFormData = false }) => {
  const { accessToken } = useSelector((state) => state?.authReducer);
  const [isLoading, setIsLoading] = useState(false);

  const mutation = useMutation({
    mutationFn: async (data) => {
      setIsLoading(true);
      try {
        return await deleteData({
          ...data,
          accessToken,
          apiURL,
          isFormData,
        });
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess,
  });

  return { ...mutation, isLoading };
};
